import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { AppState } from './state';
import { Menu, Role } from '@/models';
import { ApplicationConfiguration, Api, ApiResult, Status, SystemStatus, SystemStatusType } from '@/types';
import { ToastProgrammatic as Toaster } from 'buefy';
import { SnackbarProgrammatic as Snackbar, NotificationProgrammatic as Notification } from 'buefy';
import { BNotificationConfig } from 'buefy/types/components';
import { addMinutes, isAfter } from 'date-fns';
// import tracer from '@/core/tracer';
import menu from '../../assets/menu.json';

type StoreContext = BareActionContext<AppState, RootState>;

const appMenu: Menu[] = menu as Menu[];

const thisState: AppState = {
   applicationConfiguration: {} as ApplicationConfiguration,
   isNavBarVisible: true,
   isFooterBarVisible: true,
   isAsideVisible: true,
   isAsideMobileExpanded: false,
   isDarkMode: false,
   isLoading: false,
   menu: appMenu,
   defaultPageSize: 20,
   maxPageSize: 2147483647,
   activeMenu: undefined,
   systemStatus: { status: SystemStatusType.None, statusDescription: '', message: '' } as SystemStatus,
   systemStatusLastCheck: undefined,
};

const moduleBuilder = getStoreBuilder<RootState>().module('app', thisState);

const thisModule = {
   getters: {
      applicationConfiguration(state: AppState): ApplicationConfiguration {
         return state.applicationConfiguration;
      },

      isNavBarVisible(state: AppState): boolean {
         return state.isNavBarVisible;
      },

      isDarkMode(state: AppState): boolean {
         return state.isDarkMode;
      },

      isFooterBarVisible(state: AppState): boolean {
         return state.isFooterBarVisible;
      },

      isAsideVisible(state: AppState): boolean {
         return state.isAsideVisible;
      },

      isAsideMobileExpanded(state: AppState): boolean {
         return state.isAsideMobileExpanded;
      },

      isLoading(state: AppState): boolean {
         return state.isLoading;
      },

      roles(state: AppState): Role[] {
         return state.applicationConfiguration.roles;
      },

      menu(state: AppState): Menu[] {
         return state.menu;
      },

      pageSize(state: AppState): number {
         return state.defaultPageSize;
      },

      maxPageSize(state: AppState): number {
         return state.maxPageSize;
      },

      documentStatus(state: AppState) {
         return (status: number) => state.applicationConfiguration.documentStatus.filter((x) => x.code === status)[0] ||
            { code: 0, shortDescription: '', description: '' } as Status;
      },

      systemStatus(state: AppState) {
         return state.systemStatus || { status: SystemStatusType.None, statusDescription: '' } as SystemStatus;
      },
   },

   actions: {
      async checkSystemStatusAsync(context: StoreContext): Promise<void> {

         // Status is checked every 15 minutes
         if (context.state.systemStatusLastCheck && !isAfter(new Date(), addMinutes(context.state.systemStatusLastCheck, 15))) {
            return;
         }

         // app.info({ message: 'Checking system status'});

         return await axios.get<SystemStatus>(Api.BaseUrl + `systemstatus/check`)
            .then((response) => {
               commitSetSystemStatus(response.data);
            });
      },

      async fetchSystemTimetableAsync(context: StoreContext): Promise<void> {
         return await axios.get<SystemStatus>(Api.BaseUrl + `systemstatus/timetable`)
            .then((response) => {
               commitSetSystemTimetable(response.data);
            });
      },

      async getConfigurationAsync(context: StoreContext): Promise<void> {
         return await axios.get<ApplicationConfiguration>(Api.BaseUrl + `configuration`)
            .then((response) => {
               const systemStatus = response.data.systemStatus;
               commitApplicationConfiguration(response.data);
               commitSetSystemStatus(systemStatus);
            });
      },

      activateMenu(context: StoreContext, currentMenu: Menu): void {
         commitSetActiveMenu(currentMenu);
      },

      activateMenuById(context: StoreContext, payload: { rootId: number, subMenuId: number }): void {

         if (!context.state.menu) return;

         if (context.state.activeMenu && context.state.activeMenu.id === payload.subMenuId) return;

         const root = context.state.menu.filter((m) => m.id === payload.rootId);
         if (!root || root.length === 0 || !root[0].menu) return;

         const subMenu = root[0].menu.filter((m) => m.id === payload.subMenuId);
         if (!subMenu || subMenu.length === 0) return;

         commitSetActiveMenu(subMenu[0]);
      },

      setDarkMode(context: StoreContext): void {
         commitSetDarkMode();
      },

      success(context: StoreContext, payload: { message: string, timeout?: number }): void {

         if (!payload || !payload.message) { return; }

         Toaster.open({ duration: payload.timeout || 3000, message: payload.message, position: 'is-bottom', type: 'is-success', queue: false });
      },

      info(context: StoreContext, payload: { message: string, timeout?: number }): void {

         if (!payload || !payload.message) { return; }

         Toaster.open({ duration: payload.timeout || 3000, message: payload.message, position: 'is-bottom', type: 'is-info', queue: false });
      },

      error(context: StoreContext, payload: { message: string, timeout?: number }): void {

         if (!payload || !payload.message) { return; }

         const config: BNotificationConfig = {
            message: payload.message,
            type: 'is-danger',
            position: 'is-top-right',
            queue: false,
         };

         if (payload.timeout) {
            config.duration = payload.timeout;
         } else {
            // config.indefinite = true;
            config.duration = 5000;
         }

         Notification.open(config);
      },

      errors(context: StoreContext, payload: { status: number, result: ApiResult, ignoreNotFound: boolean }): void {

         if (!payload) { return; }

         if (payload.status === 404) {
            if (payload.ignoreNotFound) {
               return;
            } else {
               Toaster.open({ duration: 2000, message: 'Elemento non trovato.', position: 'is-bottom', type: 'is-warning', queue: false });
               return;
            }
         }

         if (payload.status === 403) {
            Toaster.open({ duration: 7000, message: 'Autorizzazioni insufficienti per l\'operazione selezionata.', position: 'is-top', type: 'is-danger', queue: false });
            return;
         }

         const apiResult = payload.result;

         if (apiResult && apiResult.errors) {
            let messages = '';

            apiResult.errors.forEach((error) => {
               const message = !!error.field ? `${error.field} - ${error.message}` : `${error.message}`;
               messages += `<li>${message}</li>`;
            });

            if (messages) {
               Snackbar.open({ duration: 7000, message: messages, position: 'is-top-right', type: 'is-danger', queue: false, actionText: 'OK' });
            }
         }

         if (apiResult && apiResult.detail) {
            Snackbar.open({ duration: 6000, message: apiResult.detail, position: 'is-top-right', type: 'is-danger', queue: false, actionText: 'OK' });
         }

      },

      warning(context: StoreContext, payload: { message: string, timeout?: number }): void {

         if (!payload || !payload.message) { return; }

         Toaster.open({ duration: payload.timeout || 6000, message: payload.message, position: 'is-bottom', type: 'is-warning', queue: false });
      },

      warnings(context: StoreContext, payload: { messages: string[], timeout?: number }): void {

         if (!payload || !payload.messages) { return; }

         payload.messages.forEach((message) => {
            Toaster.open({ duration: payload.timeout || 6000, message, position: 'is-bottom', type: 'is-warning', queue: false });
         });
      },

      toggleMenuMobile(context: StoreContext, payload: boolean | undefined): void {
         commitSetAsideMobileStateToggle(payload);
      },

      isLoading(context: StoreContext, payload: { isLoading: boolean }): void {
         commitSetIsLoading(payload);
      },
   },

   mutations: {
      setApplicationConfiguration(state: AppState, payload: ApplicationConfiguration) {
         state.applicationConfiguration = payload;
         Object.freeze(state.applicationConfiguration.systemStatus);
      },

      setAsideMobileStateToggle(state: AppState, payload: boolean | undefined) {
         const hasAsideMobileExpanded = 'has-aside-mobile-expanded';
         // const hasAsideExpanded = 'has-aside-expanded';

         let isShow = false;

         if (payload !== undefined) {
            isShow = payload;
         } else {
            isShow = !state.isAsideMobileExpanded;
         }

         const element = document.documentElement.classList;

         if (isShow) {
            element.add(hasAsideMobileExpanded);
            // element.add(hasAsideExpanded);
         } else {
            element.remove(hasAsideMobileExpanded);
            // element.remove(hasAsideExpanded);
         }

         state.isAsideMobileExpanded = isShow;
      },

      setIsLoading(state: AppState, payload: { isLoading: boolean }) {
         state.isLoading = payload.isLoading;
      },

      setActiveMenu(state: AppState, currentMenu: Menu) {
         if (state.activeMenu) {
            state.activeMenu.isActive = false;
         }
         currentMenu.isActive = true;
         state.activeMenu = currentMenu;
      },

      setDarkMode(state: AppState) {

         const htmlClassName = 'is-dark-mode-active';

         state.isDarkMode = !state.isDarkMode;

         if (state.isDarkMode) {
            document.documentElement.classList.add(htmlClassName);
         } else {
            document.documentElement.classList.remove(htmlClassName);
         }
      },

      setSystemStatus(state: AppState, status: SystemStatus) {
         if (!state.systemStatus) {
            state.systemStatus = {} as SystemStatus;
         }
         state.systemStatus.status = status.status;
         state.systemStatus.statusDescription = status.statusDescription;
         state.systemStatus.message = status.message;

         state.systemStatusLastCheck = new Date();
      },

      setSystemTimetable(state: AppState, status: SystemStatus) {
         state.systemStatus = status;
         state.systemStatusLastCheck = new Date();
      },

   },
};

// private getters
const getApplicationConfiguration = moduleBuilder.read(thisModule.getters.applicationConfiguration, 'applicationConfiguration');
const getIsNavBarVisible = moduleBuilder.read(thisModule.getters.isNavBarVisible, 'isNavBarVisible');
const getIsDarkMode = moduleBuilder.read(thisModule.getters.isDarkMode, 'isDarkMode');
const getIsFooterBarVisible = moduleBuilder.read(thisModule.getters.isFooterBarVisible, 'isFooterBarVisible');
const getIsAsideVisible = moduleBuilder.read(thisModule.getters.isAsideVisible, 'isAsideVisible');
const getIsAsideMobileExpanded = moduleBuilder.read(thisModule.getters.isAsideMobileExpanded, 'isAsideMobileExpanded');
const getIsLoading = moduleBuilder.read(thisModule.getters.isLoading, 'isLoading');
const getRoles = moduleBuilder.read(thisModule.getters.roles, 'getRoles');
const getMenu = moduleBuilder.read(thisModule.getters.menu, 'getMenu');
const getPageSize = moduleBuilder.read(thisModule.getters.pageSize, 'getPageSize');
const getMaxPageSize = moduleBuilder.read(thisModule.getters.maxPageSize, 'getMaxPageSize');
const getSystemStatus = moduleBuilder.read(thisModule.getters.systemStatus, 'systemStatus');

// private mutations
const commitApplicationConfiguration = moduleBuilder.commit(thisModule.mutations.setApplicationConfiguration, 'setApplicationConfiguration');
const commitSetAsideMobileStateToggle = moduleBuilder.commit(thisModule.mutations.setAsideMobileStateToggle, 'setAsideMobileStateToggle');
const commitSetIsLoading = moduleBuilder.commit(thisModule.mutations.setIsLoading, 'setIsLoading');
const commitSetActiveMenu = moduleBuilder.commit(thisModule.mutations.setActiveMenu, 'setActiveMenu');
const commitSetDarkMode = moduleBuilder.commit(thisModule.mutations.setDarkMode, 'setIsDarkMsetDarkModeodeActive');
const commitSetSystemStatus = moduleBuilder.commit(thisModule.mutations.setSystemStatus, 'setSystemStatus');
const commitSetSystemTimetable = moduleBuilder.commit(thisModule.mutations.setSystemTimetable, 'setSystemTimetable');

const app = {

   // getters (wrapped as real getters)
   get applicationConfiguration() { return getApplicationConfiguration(); },

   get isNavBarVisible() { return getIsNavBarVisible(); },
   get isFooterBarVisible() { return getIsFooterBarVisible(); },
   get isAsideVisible() { return getIsAsideVisible(); },
   get isAsideMobileExpanded() { return getIsAsideMobileExpanded(); },
   get isLoading() { return getIsLoading(); },
   get roles() { return getRoles(); },
   get menu() { return getMenu(); },
   get pageSize() { return getPageSize(); },
   get maxPageSize() { return getMaxPageSize(); },
   get isDarkMode() { return getIsDarkMode(); },
   get systemStatus() { return getSystemStatus(); },

   success: moduleBuilder.dispatch(thisModule.actions.success, 'success'),
   info: moduleBuilder.dispatch(thisModule.actions.info, 'info'),
   error: moduleBuilder.dispatch(thisModule.actions.error, 'error'),
   errors: moduleBuilder.dispatch(thisModule.actions.errors, 'errors'),
   warning: moduleBuilder.dispatch(thisModule.actions.warning, 'warning'),
   warnings: moduleBuilder.dispatch(thisModule.actions.warnings, 'warnings'),
   toggleMenuMobile: moduleBuilder.dispatch(thisModule.actions.toggleMenuMobile, 'toggleMenuMobile'),

   loading: moduleBuilder.dispatch(thisModule.actions.isLoading, 'isLoading'),
   getConfigurationAsync: moduleBuilder.dispatch(thisModule.actions.getConfigurationAsync, 'getConfigurationAsync'),
   activateMenu: moduleBuilder.dispatch(thisModule.actions.activateMenu, 'activateMenu'),
   activateMenuById: moduleBuilder.dispatch(thisModule.actions.activateMenuById, 'activateMenuById'),
   setDarkMode: moduleBuilder.dispatch(thisModule.actions.setDarkMode, 'setDarkMode'),
   checkSystemStatusAsync: moduleBuilder.dispatch(thisModule.actions.checkSystemStatusAsync, 'checkSystemStatusAsync'),
   fetchSystemTimetableAsync: moduleBuilder.dispatch(thisModule.actions.fetchSystemTimetableAsync, 'fetchSystemTimetableAsync'),

   documentStatus: moduleBuilder.read(thisModule.getters.documentStatus, 'documentStatus'),
};

export default app;
